/**
 * 文件上传服务
 * 处理文件上传相关的API请求
 */

import api from './api';
import { getCurrentUser } from './authService';
import endpoints from './endpoints';

/**
 * 上传单个文件
 * @param {File} file - 要上传的文件
 * @param {Object} options - 上传选项
 * @returns {Promise} - 上传结果
 */
export const uploadFile = async (file, options = {}) => {
  try {
    console.log('开始上传文件:', file.name, file.type, file.size);
    
    // 检查是否使用mock数据
    const useMockData = import.meta.env.VITE_USE_MOCK_DATA === 'true';
    if (useMockData) {
      console.log('使用mock数据模拟文件上传');
      // 模拟上传延迟
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // 返回mock响应
      return {
        file_id: `file_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        status: "success",
        url: `https://mock-storage.example.com/files/${file.name}`,
        filename: file.name,
        content_type: file.type,
        size: file.size
      };
    }
    
    // 创建FormData对象，符合v2 API格式
    const formData = new FormData();
    formData.append('file', file);

    // v2 API根据文件类型需要不同的必需字段
    if (file.type.startsWith('image/')) {
      // 图片需要 alt_text 字段（必需）
      formData.append('alt_text', options.alt_text || options.description || `Uploaded image: ${file.name}`);

      // 可选：添加图片说明
      if (options.caption || options.description) {
        formData.append('caption', options.caption || options.description);
      }
    } else if (file.type.startsWith('video/')) {
      // 视频需要 title 字段（必需）
      formData.append('title', options.title || options.description || file.name);
    } else if (file.type === 'application/pdf') {
      // PDF文件需要 title 字段（必需）
      formData.append('title', options.title || options.description || file.name);
      
      // 可选：添加PDF描述
      if (options.description) {
        formData.append('description', options.description);
      }
    } else {
      // 其他文件类型，添加通用字段
      formData.append('title', options.title || options.description || file.name);
      
      // 可选：添加描述
      if (options.description) {
        formData.append('description', options.description);
      }
    }

    // 如果需要其他字段，可以直接添加
    if (options.tags) {
      formData.append('tags', JSON.stringify(options.tags));
    }
    formData.append('status', 'published');
    // 上传端点
    const uploadEndpoint = endpoints.attachments.upload;
    console.log('上传文件到:', uploadEndpoint);
    
    // 使用统一的API工具发送请求
    const response = await api.upload(uploadEndpoint, formData, '');
    
    // 映射API响应数据结构为前端期望的格式
    const result = {
      // 保持原有的字段结构以兼容现有代码
      file_id: response?.id || response.id,
      status: "success",
      url: response?.fields?.file_url || response?.url || response.url || response?.file_url,
      file_url: response.fields?.file_url || response.url || response.url || response?.file_url,
      filename: file.name,
      content_type: response?.fields?.mime_type || response?.content_type || file.type,
      size: response?.fields?.file_size || response?.size || file.size,
      
      // 添加新的字段映射
      id: response?.id || response.id,
      alt_text: response?.fields?.alt_text || options.alt_text,
      format: response?.fields?.format,
      permissions: response?.permissions,
      inserted_at: response?.inserted_at,
      updated_at: response?.updated_at,
      author_id: response?.author_id,
      interactions: response?.interactions,
    };
    
    console.log('文件上传成功，映射后的结果:', result);
    return result;
  } catch (error) {
    console.error('文件上传错误:', error);
    throw error;
  }
};

/**
 * 批量上传文件
 * @param {FileList|Array} files - 要上传的文件列表
 * @param {Object} options - 上传选项
 * @returns {Promise} - 上传结果数组
 */
export const uploadFiles = async (files, options = {}) => {
  try {
    console.log('开始批量上传文件:', files.length, '个文件');
    
    // 并行上传所有文件
    const uploadPromises = Array.from(files).map(file => uploadFile(file, options));
    const results = await Promise.all(uploadPromises);
    
    console.log('批量上传完成:', results);
    return results;
  } catch (error) {
    console.error('批量上传失败:', error);
    throw error;
  }
};

/**
 * 删除文件
 * @param {string} fileId - 文件ID
 * @returns {Promise} - 删除结果
 */
export const deleteFile = async (fileId) => {
  try {
    console.log('删除文件:', fileId);
    
    // 检查是否使用mock数据
    const useMockData = import.meta.env.VITE_USE_MOCK_DATA === 'true';
    if (useMockData) {
      console.log('使用mock数据模拟文件删除');
      await new Promise(resolve => setTimeout(resolve, 500));
      return { status: "success", message: "文件删除成功" };
    }
    
    // 实际的删除请求（如果后端支持）
    const currentUser = getCurrentUser();
    const userId = currentUser?.id || '';
    
    if (!userId) {
      throw new Error('无法获取当前用户ID，请重新登录');
    }
    
    const deleteEndpoint = endpoints.attachments.delete(fileId);
    console.log('删除文件从:', deleteEndpoint);
    
    const result = await api.delete(deleteEndpoint, null, '', 'DeleteFileResponse');
    console.log('文件删除成功:', result);
    return result;
  } catch (error) {
    console.error('文件删除错误:', error);
    throw error;
  }
};

const uploadService = {
  uploadFile,
  uploadFiles,
  deleteFile,
};

export default uploadService;